
import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Search, FilterIcon, X } from "lucide-react";

interface FilterControlsProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  statusFilter: "ALL" | "WAITING" | "APPROVED" | "REJECTED" | "NO_PROBLEM_REPORT";
  setStatusFilter: (status: "ALL" | "WAITING" | "APPROVED" | "REJECTED" | "NO_PROBLEM_REPORT") => void;
  showOtherSubjectsOnly: boolean;
  setShowOtherSubjectsOnly: (show: boolean) => void;
  onResetFilters: () => void;
}

export const FilterControls: React.FC<FilterControlsProps> = ({
  searchQuery,
  setSearchQuery,
  statusFilter,
  setStatusFilter,
  showOtherSubjectsOnly,
  setShowOtherSubjectsOnly,
  onResetFilters
}) => {
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const hasActiveFilters = searchQuery !== "" || statusFilter !== "ALL" || showOtherSubjectsOnly;

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchQuery(localSearchQuery);
  };

  const handleClearSearch = () => {
    setLocalSearchQuery("");
    setSearchQuery("");
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-4">
        <form onSubmit={handleSearchSubmit} className="flex gap-2 w-full md:w-1/3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              placeholder="ค้นหาโดยชื่อนักเรียน, หัวข้อ, ครูผู้สอน..."
              value={localSearchQuery}
              onChange={(e) => setLocalSearchQuery(e.target.value)}
              className="pl-9"
            />
            {localSearchQuery && (
              <Button 
                type="button"
                variant="ghost" 
                size="sm" 
                className="absolute right-1 top-1 h-8 w-8 p-0"
                onClick={handleClearSearch}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          <Button type="submit" variant="default" size="default">
            <Search className="h-4 w-4 mr-1" />
            ค้นหา
          </Button>
        </form>
        
        <div className="flex items-center gap-2">
          <FilterIcon className="h-4 w-4 text-gray-400" />
          <Select 
            value={statusFilter} 
            onValueChange={(value) => setStatusFilter(value as "ALL" | "WAITING" | "APPROVED" | "REJECTED" | "NO_PROBLEM_REPORT")}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="เลือกสถานะ" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">ทุกสถานะ</SelectItem>
              <SelectItem value="WAITING">รอดำเนินการ</SelectItem>
              <SelectItem value="APPROVED">อนุมัติแล้ว</SelectItem>
              <SelectItem value="REJECTED">ไม่อนุมัติ</SelectItem>
              <SelectItem value="NO_PROBLEM_REPORT">ไม่ได้รายงานปัญหา</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center space-x-2">
          <Checkbox 
            id="other-subjects" 
            checked={showOtherSubjectsOnly}
            onCheckedChange={(checked) => setShowOtherSubjectsOnly(!!checked)}
          />
          <Label htmlFor="other-subjects">แสดงเฉพาะหัวข้ออื่นๆ</Label>
        </div>
        
        {hasActiveFilters && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onResetFilters}
            className="text-blue-600"
          >
            <X className="mr-1 h-4 w-4" />
            ล้างตัวกรอง
          </Button>
        )}
      </div>
    </div>
  );
};
